import { useEffect, useState } from "react";
import api, { formatCLP, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
    Package, Users, Settings, BarChart3, ShoppingBag, Plus, Pencil, Trash2, Eye, X,
    DollarSign, Boxes, ClipboardList, Star, Mail, Gift, Coins, Power, PowerOff, ImagePlus,
    MessageCircle, FolderTree, Image as ImageIcon, Megaphone, HelpCircle, Target, Trash,
    Building2, Truck, Bell, Banknote,
} from "lucide-react";

const STATUSES = ["pendiente", "pagado", "enviado", "entregado", "retirado", "cancelado", "devolucion"];

const STATUS_LABELS = {
    pendiente: "Pendiente",
    pagado: "Pagado",
    enviado: "Enviado",
    entregado: "Entregado",
    retirado: "Retirado en tienda",
    cancelado: "Cancelado",
    devolucion: "Devolución",
};

// Categorías ahora se cargan dinámicas desde /api/categories

// Utilidades compartidas
const fileToBase64 = (file, maxMB = 3) =>
    new Promise((resolve, reject) => {
        if (!file) return reject(new Error("Sin archivo"));
        if (file.size > maxMB * 1024 * 1024) return reject(new Error(`Máx ${maxMB}MB`));
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("Error leyendo la imagen"));
        r.readAsDataURL(file);
    });

const Stat = ({ icon: Icon, label, value, color }) => {
    const isDark = color === "bg-cocoa";
    const fg = isDark ? "text-white" : "text-cocoa";
    return (
        <div className={`${color} ${fg} rounded-3xl p-5 shadow-md`}>
            <div className="flex items-center justify-between mb-2">
                <Icon className="w-6 h-6 opacity-80" />
            </div>
            <div className="text-3xl font-display font-bold">{value}</div>
            <div className="text-xs uppercase tracking-widest opacity-90">{label}</div>
        </div>
    );
};

function Dashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, []);
    if (!stats) return <div className="py-10 text-center text-muted-foreground">Cargando…</div>;
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Stat icon={ShoppingBag} label="Pedidos" value={stats.total_orders} color="bg-mexred" />
                <Stat icon={Users} label="Usuarios" value={stats.total_users} color="bg-marigold" />
                <Stat icon={Boxes} label="Productos" value={stats.total_products} color="bg-marigold" />
                <Stat icon={ClipboardList} label="Pendientes" value={stats.pending_orders} color="bg-cocoa" />
                <Stat icon={DollarSign} label="Ventas pagadas" value={formatCLP(stats.paid_sales)} color="bg-mexyellow" />
            </div>
            <div className="bg-white rounded-3xl border border-border p-6">
                <h3 className="font-display text-xl font-semibold mb-4">Pedidos recientes</h3>
                {stats.recent_orders.length === 0 ? (
                    <p className="text-muted-foreground">No hay pedidos aún.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="py-2">ID</th>
                                    <th>Cliente</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th>Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.recent_orders.map((o) => (
                                    <tr key={o.id} className="border-b border-border/40">
                                        <td className="py-2 font-mono text-xs">{o.id.slice(0, 8)}</td>
                                        <td>{o.customer.full_name}</td>
                                        <td className="text-cocoa font-semibold">{formatCLP(o.total)}</td>
                                        <td><span className="text-xs uppercase">{o.status}</span></td>
                                        <td className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("es-CL")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const emptyProduct = {
    name: "", brand: "", category: "Dulces enchilados", price: 0, stock: 0,
    description: "", image: "", image2: "", featured: false, branch_stock: {},
};

function ProductsAdmin() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [branches, setBranches] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/products").then(({ data }) => setProducts(data));
    const loadCats = () => api.get("/categories").then(({ data }) => setCategories(data.categories || []));
    const loadBranches = () => api.get("/branches").then(({ data }) => setBranches(data));
    useEffect(() => { load(); loadCats(); loadBranches(); }, []);

    const onFile = async (slot, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const b64 = await fileToBase64(file, 3);
            setEditing((p) => ({ ...p, [slot]: b64 }));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const updateBranchStock = (bid, val) => {
        const v = Math.max(0, parseInt(val || "0"));
        setEditing((p) => {
            const bs = { ...(p.branch_stock || {}), [bid]: v };
            const total = Object.values(bs).reduce((a, b) => a + (parseInt(b) || 0), 0);
            return { ...p, branch_stock: bs, stock: total };
        });
    };

    const onSave = async () => {
        try {
            // Recompute total stock from branch_stock
            const bs = editing.branch_stock || {};
            const totalStock = Object.values(bs).reduce((a, b) => a + (parseInt(b) || 0), 0);
            const payload = {
                ...editing,
                price: Number(editing.price),
                stock: totalStock || Number(editing.stock || 0),
                branch_stock: bs,
                image: editing.image || "",
                image2: editing.image2 || "",
            };
            if (editing.id) await api.put(`/products/${editing.id}`, payload);
            else await api.post("/products", payload);
            toast.success("Guardado");
            setOpen(false); setEditing(null); load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("¿Eliminar este producto?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Eliminado");
        load();
    };

    const onClearAllImages = async () => {
        if (!window.confirm("¿Borrar TODAS las fotos de TODOS los productos? Esta acción no se puede deshacer.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/products/clear-images");
            toast.success(`Se limpiaron ${data.cleared} productos`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display text-2xl font-semibold">Productos ({products.length})</h2>
                <div className="flex gap-2 flex-wrap">
                    <button
                        data-testid="admin-clear-images-btn"
                        onClick={onClearAllImages}
                        disabled={busy}
                        className="bg-white text-cocoa border border-cocoa/30 hover:bg-cocoa/5 rounded-full px-4 py-2 font-semibold flex items-center gap-2 text-sm"
                    >
                        <Trash className="w-4 h-4" /> Borrar todas las fotos
                    </button>
                    <button
                        data-testid="admin-add-product-btn"
                        onClick={() => { setEditing({ ...emptyProduct, category: categories[0] || "Dulces enchilados" }); setOpen(true); }}
                        className="bg-mexred hover:bg-mexred_hover text-cocoa rounded-full px-4 py-2 font-semibold flex items-center gap-2 shadow-md"
                    >
                        <Plus className="w-4 h-4" /> Nuevo producto
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">Fotos</th>
                                <th>Nombre</th>
                                <th>Categoría</th>
                                <th>Precio</th>
                                <th>Stock</th>
                                <th>Destacado</th>
                                <th className="text-right pr-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id} className="border-t border-border/40">
                                    <td className="p-2">
                                        <div className="flex gap-1">
                                            {p.image ? <img src={p.image} alt="" className="w-10 h-10 object-cover rounded-lg" /> : <div className="w-10 h-10 rounded-lg bg-muted" />}
                                            {p.image2 ? <img src={p.image2} alt="" className="w-10 h-10 object-cover rounded-lg" /> : <div className="w-10 h-10 rounded-lg bg-muted/50" />}
                                        </div>
                                    </td>
                                    <td className="font-medium">{p.name}</td>
                                    <td>{p.category}</td>
                                    <td className="text-cocoa font-semibold">{formatCLP(p.price)}</td>
                                    <td>{p.stock}</td>
                                    <td>{p.featured ? <Star className="w-4 h-4 text-mexyellow fill-mexyellow" /> : "—"}</td>
                                    <td className="text-right pr-3">
                                        <button onClick={() => { setEditing({ ...p }); setOpen(true); }} className="p-2 hover:bg-muted rounded-lg">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => onDelete(p.id)} className="p-2 hover:bg-mexred/10 hover:text-cocoa rounded-lg">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <input className="w-full px-3 py-2 rounded-xl border border-border" placeholder="Nombre"
                                value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                            <input className="w-full px-3 py-2 rounded-xl border border-border" placeholder="Marca"
                                value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
                            <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="grid grid-cols-2 gap-2">
                                <input type="number" className="px-3 py-2 rounded-xl border border-border" placeholder="Precio CLP"
                                    value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
                                <div className="px-3 py-2 rounded-xl border border-border bg-muted/30 text-sm flex items-center justify-between">
                                    <span className="text-muted-foreground">Stock total:</span>
                                    <span className="font-semibold text-cocoa">{Object.values(editing.branch_stock || {}).reduce((a, b) => a + (parseInt(b) || 0), 0) || 0}</span>
                                </div>
                            </div>

                            {branches.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                    <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1">
                                        <Building2 className="w-3 h-3" /> Stock por sucursal
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {branches.map((b) => (
                                            <div key={b.id} className="flex items-center gap-2">
                                                <span className="text-xs text-cocoa flex-1 truncate">{b.name}</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={(editing.branch_stock || {})[b.id] ?? 0}
                                                    onChange={(e) => updateBranchStock(b.id, e.target.value)}
                                                    className="w-20 px-2 py-1 rounded-lg border border-amber-300 text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Dos fotos por producto - solo subida de archivo */}
                            <div className="grid grid-cols-2 gap-3">
                                {["image", "image2"].map((slot, idx) => (
                                    <div key={slot}>
                                        <label className="text-sm font-medium text-cocoa">Foto {idx + 1}{idx === 0 ? " (principal)" : ""}</label>
                                        <div className="mt-1 border-2 border-dashed border-mexred/40 rounded-2xl p-3 bg-mexred/5 aspect-square flex items-center justify-center relative">
                                            {editing[slot] ? (
                                                <>
                                                    <img src={editing[slot]} alt="" className="w-full h-full object-contain rounded-xl bg-white" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing({ ...editing, [slot]: "" })}
                                                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow hover:bg-white"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="flex flex-col items-center justify-center cursor-pointer py-2 text-center">
                                                    <ImagePlus className="w-6 h-6 text-cocoa mb-1" />
                                                    <span className="text-[11px] text-cocoa font-medium">Subir imagen</span>
                                                    <span className="text-[10px] text-muted-foreground">PNG/JPG · 3MB</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(e) => onFile(slot, e)}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <textarea className="w-full px-3 py-2 rounded-xl border border-border min-h-20" placeholder="Descripción"
                                value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.featured}
                                    onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                                Marcar como destacado
                            </label>
                            <button data-testid="admin-save-product-btn" onClick={onSave} className="w-full bg-mexred text-cocoa rounded-full py-2.5 font-semibold">Guardar</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function OrdersAdmin() {
    const [orders, setOrders] = useState([]);
    const [branches, setBranches] = useState([]);
    const [viewing, setViewing] = useState(null);
    const [shipFor, setShipFor] = useState(null);
    const [shipForm, setShipForm] = useState({ tracking_number: "", carrier: "", photo_base64: "" });
    const [shipBusy, setShipBusy] = useState(false);

    const load = () => api.get("/orders").then(({ data }) => setOrders(data));
    useEffect(() => {
        load();
        api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    }, []);

    const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

    const updateStatus = async (id, status) => {
        await api.put(`/orders/${id}/status`, { status });
        toast.success("Estado actualizado");
        load();
        if (viewing?.id === id) setViewing({ ...viewing, status });
    };

    const openShipping = (o) => {
        setShipFor(o);
        setShipForm({
            tracking_number: o.shipping_info?.tracking_number || "",
            carrier: o.shipping_info?.carrier || "",
            photo_base64: o.shipping_info?.photo_base64 || "",
        });
    };

    const handleShipPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const b64 = await fileToBase64(f, 5);
            setShipForm((s) => ({ ...s, photo_base64: b64 }));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const saveShipping = async () => {
        if (!shipFor) return;
        setShipBusy(true);
        try {
            await api.put(`/orders/${shipFor.id}/shipping`, shipForm);
            toast.success("Información de envío guardada");
            setShipFor(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setShipBusy(false);
        }
    };

    const shareWhatsApp = (o) => {
        const items = (o.items || [])
            .map((it) => `• ${it.quantity}× ${it.name} — ${formatCLP(it.price * it.quantity)}`)
            .join("\n");
        const reward = o.redeemed_reward
            ? `\n🎁 Canje: ${o.redeemed_reward.name} (${o.redeemed_reward.points_cost} pts)`
            : "";
        const delivery = o.delivery_type === "pickup"
            ? `\n🏪 Retiro en: ${branchName(o.pickup_branch_id)}`
            : `\n🚚 Envío a domicilio`;
        const text =
            `🛒 *Pedido #${o.id.slice(0, 8)}* — La Dulcería Mexicana\n` +
            `\n👤 ${o.customer.full_name}\n📧 ${o.customer.email}\n📱 ${o.customer.phone}\n🆔 RUT: ${o.customer.rut}\n📍 ${o.customer.address}` +
            (o.customer.notes ? `\n📝 ${o.customer.notes}` : "") +
            `\n\n*Productos:*\n${items}` +
            reward + delivery +
            `\n\n💰 *Total:* ${formatCLP(o.total)}` +
            `\n📦 Estado: ${STATUS_LABELS[o.status] || o.status}` +
            `\n🕒 ${new Date(o.created_at).toLocaleString("es-CL")}`;
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const statusBadgeColor = (s) => ({
        pendiente: "bg-amber-100 text-amber-800",
        pagado: "bg-emerald-100 text-emerald-800",
        enviado: "bg-blue-100 text-blue-800",
        entregado: "bg-green-100 text-green-800",
        retirado: "bg-green-100 text-green-800",
        cancelado: "bg-red-100 text-red-800",
        devolucion: "bg-orange-100 text-orange-800",
    }[s] || "bg-gray-100 text-gray-700");

    return (
        <div className="space-y-4">
            <h2 className="font-display text-2xl font-semibold">Pedidos ({orders.length})</h2>
            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">ID</th>
                                <th>Cliente</th>
                                <th>Entrega</th>
                                <th>Total</th>
                                <th>Estado</th>
                                <th>Fecha</th>
                                <th className="text-right pr-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} className="border-t border-border/40">
                                    <td className="p-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                                    <td>
                                        <div className="font-medium">{o.customer.full_name}</div>
                                        <div className="text-xs text-muted-foreground">{o.customer.phone}</div>
                                    </td>
                                    <td className="text-xs">
                                        {o.delivery_type === "pickup" ? (
                                            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-2 py-1 rounded-full">🏪 {branchName(o.pickup_branch_id)}</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2 py-1 rounded-full">🚚 Envío</span>
                                        )}
                                    </td>
                                    <td className="text-cocoa font-semibold">{formatCLP(o.total)}</td>
                                    <td>
                                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                            <SelectTrigger className={`w-36 h-8 text-xs ${statusBadgeColor(o.status)}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("es-CL")}</td>
                                    <td className="text-right pr-3 whitespace-nowrap">
                                        <button
                                            onClick={() => openShipping(o)}
                                            title="Cargar info de envío / guía"
                                            className="p-2 hover:bg-blue-100 rounded-lg text-blue-700 relative"
                                        >
                                            <Truck className="w-4 h-4" />
                                            {o.shipping_info?.tracking_number && (
                                                <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-green-500 rounded-full"></span>
                                            )}
                                        </button>
                                        <button
                                            data-testid={`admin-wa-share-${o.id}`}
                                            onClick={() => shareWhatsApp(o)}
                                            title="Compartir por WhatsApp"
                                            className="p-2 hover:bg-green-100 rounded-lg text-green-700"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </button>
                                        <button data-testid={`admin-view-order-${o.id}`} onClick={() => setViewing(o)} className="p-2 hover:bg-muted rounded-lg">
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detalle */}
            <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Detalle del pedido</DialogTitle>
                    </DialogHeader>
                    {viewing && (
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-muted/30 rounded-xl p-3">
                                    <div className="text-xs uppercase text-muted-foreground">Cliente</div>
                                    <div className="font-semibold">{viewing.customer.full_name}</div>
                                    <div>RUT: {viewing.customer.rut}</div>
                                    <div>{viewing.customer.email}</div>
                                    <div>{viewing.customer.phone}</div>
                                    <div className="text-xs mt-1">{viewing.customer.address}</div>
                                    {viewing.customer.notes && <div className="text-xs italic mt-1">"{viewing.customer.notes}"</div>}
                                </div>
                                <div className="bg-muted/30 rounded-xl p-3">
                                    <div className="text-xs uppercase text-muted-foreground">Pedido</div>
                                    <div className="font-mono text-xs">{viewing.id}</div>
                                    <div>Estado: <strong>{STATUS_LABELS[viewing.status]}</strong></div>
                                    <div>Total: <strong className="text-cocoa">{formatCLP(viewing.total)}</strong></div>
                                    <div>Puntos: {viewing.points_earned} <span className="text-xs text-muted-foreground">({viewing.points_status === "awarded" ? "✅ liberados" : viewing.points_status === "revoked" ? "❌ perdidos" : "⏳ pendientes"})</span></div>
                                    <div className="mt-1">{viewing.delivery_type === "pickup" ? `🏪 Retiro en ${branchName(viewing.pickup_branch_id)}` : "🚚 Envío a domicilio"}</div>
                                </div>
                            </div>
                            {viewing.shipping_info && (viewing.shipping_info.tracking_number || viewing.shipping_info.carrier || viewing.shipping_info.photo_base64) && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                                    <div className="text-xs uppercase text-blue-800 font-semibold flex items-center gap-1"><Truck className="w-3 h-3"/> Info de envío</div>
                                    {viewing.shipping_info.carrier && <div>Empresa: <strong>{viewing.shipping_info.carrier}</strong></div>}
                                    {viewing.shipping_info.tracking_number && <div>Nº guía: <strong>{viewing.shipping_info.tracking_number}</strong></div>}
                                    {viewing.shipping_info.photo_base64 && <img src={viewing.shipping_info.photo_base64} alt="Guía" className="rounded-lg max-h-48 mt-2" />}
                                </div>
                            )}
                            <div>
                                <div className="font-semibold mb-2">Productos</div>
                                <ul className="space-y-1">
                                    {viewing.items.map((it, i) => (
                                        <li key={i} className="flex justify-between border-b border-border/40 pb-1">
                                            <span>{it.quantity}× {it.name}</span>
                                            <span className="text-cocoa">{formatCLP(it.price * it.quantity)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="font-semibold mb-2">Comprobante de pago</div>
                                {viewing.receipt_base64 ? (
                                    <img src={viewing.receipt_base64} alt="Comprobante" className="rounded-xl max-h-96 mx-auto shadow" />
                                ) : (
                                    <p className="text-muted-foreground">Sin comprobante</p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => shareWhatsApp(viewing)}
                                className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 mt-2"
                            >
                                <MessageCircle className="w-4 h-4" /> Compartir pedido por WhatsApp
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal envío */}
            <Dialog open={!!shipFor} onOpenChange={(v) => !v && setShipFor(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-blue-600" /> Información de envío</DialogTitle>
                    </DialogHeader>
                    {shipFor && (
                        <div className="space-y-3 text-sm">
                            <p className="text-xs text-muted-foreground">Pedido <span className="font-mono">{shipFor.id.slice(0, 8)}</span> — {shipFor.customer.full_name}</p>
                            <div>
                                <label className="text-xs font-medium">Empresa de envío</label>
                                <input
                                    value={shipForm.carrier}
                                    onChange={(e) => setShipForm((s) => ({ ...s, carrier: e.target.value }))}
                                    placeholder="Chilexpress, Starken, Correos…"
                                    className="w-full px-3 py-2 border border-border rounded-xl mt-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Número de guía / seguimiento</label>
                                <input
                                    value={shipForm.tracking_number}
                                    onChange={(e) => setShipForm((s) => ({ ...s, tracking_number: e.target.value }))}
                                    placeholder="Ej: 1234567890"
                                    className="w-full px-3 py-2 border border-border rounded-xl mt-1"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Foto de la guía / paquete</label>
                                <input type="file" accept="image/*" onChange={handleShipPhoto} className="w-full text-xs mt-1" />
                                {shipForm.photo_base64 && <img src={shipForm.photo_base64} alt="Foto envío" className="mt-2 rounded-lg max-h-48" />}
                            </div>
                            <button
                                onClick={saveShipping}
                                disabled={shipBusy}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-2.5 font-semibold disabled:opacity-50"
                            >
                                {shipBusy ? "Guardando…" : "Guardar"}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BranchesAdmin() {
    const [branches, setBranches] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", address: "", phone: "", note: "Este stock puede variar según la afluencia de personas en el local.", active: true, order: 0 });

    const load = () => api.get("/branches").then(({ data }) => setBranches(data));
    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing("new");
        setForm({ name: "", address: "", phone: "", note: "Este stock puede variar según la afluencia de personas en el local.", active: true, order: branches.length });
    };
    const openEdit = (b) => {
        setEditing(b.id);
        setForm({
            name: b.name || "", address: b.address || "", phone: b.phone || "",
            note: b.note || "", active: !!b.active, order: b.order || 0,
        });
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
        try {
            if (editing === "new") {
                await api.post("/branches", form);
                toast.success("Sucursal creada");
            } else {
                await api.put(`/branches/${editing}`, form);
                toast.success("Sucursal actualizada");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar esta sucursal? Se eliminará su stock de todos los productos.")) return;
        try {
            await api.delete(`/branches/${id}`);
            toast.success("Eliminada");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Building2 className="w-6 h-6 text-mexred" /> Sucursales</h2>
                <button onClick={openNew} className="bg-cocoa text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Nueva</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {branches.map((b) => (
                    <div key={b.id} className="bg-white rounded-2xl border border-border p-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-display text-lg font-semibold">{b.name}</div>
                                <div className="text-sm text-muted-foreground">{b.address}</div>
                                <div className="text-xs text-muted-foreground mt-1">📱 {b.phone}</div>
                                {b.note && <div className="text-xs italic mt-2 text-muted-foreground">📝 {b.note}</div>}
                                <div className="text-xs mt-2">Estado: {b.active ? <span className="text-green-700">Activa ✅</span> : <span className="text-red-700">Inactiva ❌</span>}</div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(b)} className="p-2 hover:bg-muted rounded-lg"><Pencil className="w-4 h-4"/></button>
                                <button onClick={() => remove(b.id)} className="p-2 hover:bg-red-100 rounded-lg text-red-600"><Trash2 className="w-4 h-4"/></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>{editing === "new" ? "Nueva sucursal" : "Editar sucursal"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre *</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-border rounded-xl mt-1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Dirección</label>
                            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-border rounded-xl mt-1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Teléfono</label>
                            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-border rounded-xl mt-1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Nota mostrada al cliente (sobre disponibilidad)</label>
                            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="w-full px-3 py-2 border border-border rounded-xl mt-1 text-sm" />
                        </div>
                        <div className="flex items-center justify-between">
                            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activa</label>
                            <div>
                                <label className="text-xs font-medium mr-2">Orden:</label>
                                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0") })} className="w-16 px-2 py-1 border border-border rounded-lg text-sm" />
                            </div>
                        </div>
                        <button onClick={save} className="w-full bg-cocoa text-white rounded-full py-2.5 font-semibold">Guardar</button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function UsersAdmin() {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/users").then(({ data }) => setUsers(data));
    useEffect(() => { load(); }, []);

    const savePoints = async () => {
        await api.put(`/users/${editing.id}/points`, { points: Number(editing.points) });
        toast.success("Puntos actualizados");
        setEditing(null);
        load();
    };

    return (
        <div className="space-y-4">
            <h2 className="font-display text-2xl font-semibold">Usuarios ({users.length})</h2>
            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">Nombre</th>
                                <th>Email</th>
                                <th>Teléfono</th>
                                <th>Rol</th>
                                <th>Puntos</th>
                                <th>Registro</th>
                                <th className="text-right pr-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-border/40">
                                    <td className="p-3 font-medium">{u.name}</td>
                                    <td>{u.email}</td>
                                    <td>{u.phone || "—"}</td>
                                    <td>
                                        <span className={`text-xs px-2 py-0.5 rounded-full uppercase font-semibold ${u.role === "admin" ? "bg-mexred text-cocoa" : "bg-muted"}`}>{u.role}</span>
                                    </td>
                                    <td className="font-bold text-marigold">{u.points || 0}</td>
                                    <td className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("es-CL")}</td>
                                    <td className="text-right pr-3">
                                        <button data-testid={`admin-edit-points-${u.id}`} onClick={() => setEditing({ ...u })} className="p-2 hover:bg-muted rounded-lg">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Editar puntos</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div className="text-sm text-muted-foreground">Usuario: <strong>{editing.name}</strong></div>
                            <input
                                data-testid="admin-points-input"
                                type="number"
                                className="w-full px-3 py-2 rounded-xl border border-border"
                                value={editing.points || 0}
                                onChange={(e) => setEditing({ ...editing, points: e.target.value })}
                            />
                            <button data-testid="admin-save-points-btn" onClick={savePoints} className="w-full bg-mexred text-cocoa rounded-full py-2.5 font-semibold">Guardar</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SettingsAdmin() {
    const [link, setLink] = useState("");
    const [instructions, setInstructions] = useState("");
    const [bank, setBank] = useState({
        bank_name: "",
        bank_account_type: "",
        bank_account_number: "",
        bank_account_holder: "",
        bank_rut: "",
        bank_email: "",
    });
    const [savingPay, setSavingPay] = useState(false);

    const [site, setSite] = useState({
        hero_image: "",
        marquee_messages: [],
        gift_threshold: 20000,
        faq: [],
        admin_notify_email: "",
    });
    const [savingSite, setSavingSite] = useState(false);

    useEffect(() => {
        api.get("/settings/payment-link").then(({ data }) => {
            setLink(data.payment_link || "");
            setInstructions(data.payment_instructions || "");
            setBank({
                bank_name: data.bank_name || "",
                bank_account_type: data.bank_account_type || "",
                bank_account_number: data.bank_account_number || "",
                bank_account_holder: data.bank_account_holder || "",
                bank_rut: data.bank_rut || "",
                bank_email: data.bank_email || "",
            });
        });
        api.get("/settings/site").then(({ data }) => {
            setSite({
                hero_image: data.hero_image || "",
                marquee_messages: data.marquee_messages || [],
                gift_threshold: data.gift_threshold || 20000,
                faq: data.faq || [],
                admin_notify_email: data.admin_notify_email || "",
            });
        });
    }, []);

    const savePay = async () => {
        setSavingPay(true);
        try {
            await api.put("/settings/payment-link", {
                payment_link: link,
                payment_instructions: instructions,
                ...bank,
            });
            toast.success("Configuración de pago guardada");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
        setSavingPay(false);
    };

    const onHeroFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const b64 = await fileToBase64(file, 5);
            setSite((s) => ({ ...s, hero_image: b64 }));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const saveSite = async () => {
        setSavingSite(true);
        try {
            await api.put("/settings/site", {
                hero_image: site.hero_image,
                marquee_messages: site.marquee_messages.filter((m) => (m || "").trim().length > 0),
                gift_threshold: Number(site.gift_threshold) || 20000,
                faq: site.faq.filter((f) => (f.question || "").trim() && (f.answer || "").trim()),
                admin_notify_email: site.admin_notify_email || "",
            });
            toast.success("Configuración del sitio guardada");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
        setSavingSite(false);
    };

    const updMarquee = (idx, v) => {
        const copy = [...site.marquee_messages];
        copy[idx] = v;
        setSite({ ...site, marquee_messages: copy });
    };
    const addMarquee = () => setSite({ ...site, marquee_messages: [...site.marquee_messages, ""] });
    const delMarquee = (idx) => setSite({ ...site, marquee_messages: site.marquee_messages.filter((_, i) => i !== idx) });

    const updFaq = (idx, field, v) => {
        const copy = [...site.faq];
        copy[idx] = { ...copy[idx], [field]: v };
        setSite({ ...site, faq: copy });
    };
    const addFaq = () => setSite({ ...site, faq: [...site.faq, { question: "", answer: "" }] });
    const delFaq = (idx) => setSite({ ...site, faq: site.faq.filter((_, i) => i !== idx) });

    return (
        <div className="space-y-6 max-w-3xl">
            <h2 className="font-display text-2xl font-semibold">Configuración general</h2>

            {/* Pago */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-4">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><DollarSign className="w-5 h-5 text-mexred" /> Pago</h3>
                <div>
                    <label className="text-sm font-medium text-cocoa">Link de pago (Mercado Pago, Webpay, etc.)</label>
                    <input
                        data-testid="admin-payment-link-input"
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border focus:border-mexred outline-none"
                        placeholder="https://..."
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                    />
                </div>
                <div>
                    <label className="text-sm font-medium text-cocoa">Instrucciones de pago (opcional)</label>
                    <textarea
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border focus:border-mexred outline-none min-h-24"
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                    />
                </div>

                {/* Datos para transferencia bancaria */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center gap-2 font-semibold text-amber-900">
                        <Banknote className="w-5 h-5" /> Datos para transferencia bancaria
                    </div>
                    <p className="text-xs text-amber-800">Estos datos se mostrarán al cliente cuando elija la opción "Pagar con transferencia" en el checkout.</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Banco</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="Banco de Chile, BCI…"
                                value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Tipo de cuenta</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="Corriente / Vista / RUT"
                                value={bank.bank_account_type} onChange={(e) => setBank({ ...bank, bank_account_type: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Nº de cuenta</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="00012345678"
                                value={bank.bank_account_number} onChange={(e) => setBank({ ...bank, bank_account_number: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">RUT titular</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="11.111.111-1"
                                value={bank.bank_rut} onChange={(e) => setBank({ ...bank, bank_rut: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-medium">Nombre titular</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="La Dulcería Mexicana SpA"
                                value={bank.bank_account_holder} onChange={(e) => setBank({ ...bank, bank_account_holder: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs font-medium">Email para enviar comprobante</label>
                            <input className="mt-1 w-full px-3 py-2 rounded-xl border border-border" placeholder="pagos@…"
                                value={bank.bank_email} onChange={(e) => setBank({ ...bank, bank_email: e.target.value })} />
                        </div>
                    </div>
                </div>
                <button
                    data-testid="admin-save-settings-btn"
                    disabled={savingPay}
                    onClick={savePay}
                    className="bg-mexred hover:bg-mexred_hover disabled:bg-muted text-cocoa rounded-full px-6 py-2.5 font-semibold"
                >
                    {savingPay ? "Guardando..." : "Guardar pago"}
                </button>
            </section>

            {/* Imagen principal (Hero) */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-4">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-marigold" /> Imagen principal (portada)</h3>
                <p className="text-xs text-muted-foreground">Esta imagen aparece en el hero de la página de inicio. Si no subes ninguna, se mostrará la imagen por defecto.</p>
                <div className="border-2 border-dashed border-mexred/40 rounded-2xl p-4 bg-mexred/5">
                    {site.hero_image ? (
                        <div className="relative">
                            <img src={site.hero_image} alt="hero" className="w-full max-h-72 object-contain rounded-xl bg-white" />
                            <button
                                type="button"
                                onClick={() => setSite({ ...site, hero_image: "" })}
                                className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white shadow"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer py-8">
                            <ImagePlus className="w-10 h-10 text-cocoa mb-2" />
                            <span className="text-sm text-cocoa font-medium">Subir imagen principal</span>
                            <span className="text-xs text-muted-foreground">PNG / JPG · máx 5MB</span>
                            <input data-testid="hero-image-input" type="file" accept="image/*" className="hidden" onChange={onHeroFile} />
                        </label>
                    )}
                </div>
            </section>

            {/* Termómetro */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-3">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Target className="w-5 h-5 text-flaggreen" /> Termómetro del carrito</h3>
                <label className="text-sm font-medium text-cocoa">Meta para regalo sorpresa (CLP)</label>
                <input
                    data-testid="gift-threshold-input"
                    type="number"
                    className="w-full px-3 py-2.5 rounded-xl border border-border outline-none"
                    value={site.gift_threshold}
                    onChange={(e) => setSite({ ...site, gift_threshold: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Cuando el total del carrito llega o supera este monto, el cliente obtiene un regalo sorpresa.</p>
            </section>

            {/* Cintillo / Marquee */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5 text-mexred" /> Cintillo de promociones</h3>
                    <button onClick={addMarquee} className="text-sm text-mexred font-semibold hover:underline">+ Agregar mensaje</button>
                </div>
                <p className="text-xs text-muted-foreground">Mensajes que giran en la franja superior. Deja al menos uno.</p>
                {site.marquee_messages.length === 0 && <p className="text-sm text-muted-foreground italic">Sin mensajes aún.</p>}
                {site.marquee_messages.map((m, i) => (
                    <div key={i} className="flex gap-2 items-start">
                        <input
                            className="flex-1 px-3 py-2 rounded-xl border border-border"
                            placeholder="🎁 Tu mensaje aquí..."
                            value={m}
                            onChange={(e) => updMarquee(i, e.target.value)}
                        />
                        <button onClick={() => delMarquee(i)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
            </section>

            {/* FAQ */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2"><HelpCircle className="w-5 h-5 text-flaggreen" /> Preguntas frecuentes (FAQ)</h3>
                    <button onClick={addFaq} className="text-sm text-mexred font-semibold hover:underline">+ Agregar pregunta</button>
                </div>
                {site.faq.length === 0 && <p className="text-sm text-muted-foreground italic">Sin preguntas aún.</p>}
                {site.faq.map((f, i) => (
                    <div key={i} className="border border-border rounded-2xl p-3 space-y-2 bg-muted/20">
                        <div className="flex gap-2">
                            <input
                                className="flex-1 px-3 py-2 rounded-xl border border-border"
                                placeholder="Pregunta (Ej: ¿Cuándo hacen los envíos?)"
                                value={f.question}
                                onChange={(e) => updFaq(i, "question", e.target.value)}
                            />
                            <button onClick={() => delFaq(i)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <textarea
                            className="w-full px-3 py-2 rounded-xl border border-border min-h-16"
                            placeholder="Respuesta..."
                            value={f.answer}
                            onChange={(e) => updFaq(i, "answer", e.target.value)}
                        />
                    </div>
                ))}
            </section>

            {/* Email de notificaciones */}
            <section className="bg-white rounded-3xl border border-border p-6 space-y-3">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Mail className="w-5 h-5 text-marigold" /> Email para notificaciones de admin</h3>
                <input
                    className="w-full px-3 py-2.5 rounded-xl border border-border"
                    type="email"
                    placeholder="ladulceriamexicana30@gmail.com"
                    value={site.admin_notify_email}
                    onChange={(e) => setSite({ ...site, admin_notify_email: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Aquí se enviará un correo cada vez que un cliente haga un pedido.</p>
            </section>

            <div className="sticky bottom-4 z-10">
                <button
                    data-testid="admin-save-site-btn"
                    onClick={saveSite}
                    disabled={savingSite}
                    className="w-full bg-mexred hover:bg-mexred_hover disabled:bg-muted text-cocoa rounded-full py-3 font-semibold shadow-lg"
                >
                    {savingSite ? "Guardando..." : "💾 Guardar configuración del sitio"}
                </button>
            </div>
        </div>
    );
}

function CategoriesAdmin() {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = () => api.get("/categories/all").then(({ data }) => setCats(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const onSave = async () => {
        if (!editing.name?.trim()) return toast.error("Nombre obligatorio");
        try {
            const payload = {
                name: editing.name.trim(),
                emoji: editing.emoji || "",
                active: !!editing.active,
                order: Number(editing.order) || 0,
            };
            if (editing.id) await api.put(`/categories/${editing.id}`, payload);
            else await api.post("/categories", payload);
            toast.success("Guardada");
            setOpen(false); setEditing(null); load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("¿Eliminar esta categoría? No se puede si tiene productos asociados.")) return;
        try {
            await api.delete(`/categories/${id}`);
            toast.success("Eliminada");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display text-2xl font-semibold">Categorías ({cats.length})</h2>
                    <p className="text-sm text-muted-foreground">Administra las categorías que aparecen en el catálogo.</p>
                </div>
                <button
                    data-testid="admin-add-category-btn"
                    onClick={() => { setEditing({ name: "", emoji: "", active: true, order: (cats.length + 1) }); setOpen(true); }}
                    className="bg-mexred hover:bg-mexred_hover text-cocoa rounded-full px-4 py-2 font-semibold flex items-center gap-2 shadow-md"
                >
                    <Plus className="w-4 h-4" /> Nueva categoría
                </button>
            </div>

            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">Orden</th>
                                <th>Emoji</th>
                                <th>Nombre</th>
                                <th>Activa</th>
                                <th className="text-right pr-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cats.length === 0 ? (
                                <tr><td colSpan={5} className="text-center p-8 text-muted-foreground">Sin categorías.</td></tr>
                            ) : cats.map((c) => (
                                <tr key={c.id} className="border-t border-border/40">
                                    <td className="p-3">{c.order}</td>
                                    <td className="text-2xl">{c.emoji}</td>
                                    <td className="font-medium">{c.name}</td>
                                    <td>{c.active ? "✅" : "—"}</td>
                                    <td className="text-right pr-3">
                                        <button onClick={() => { setEditing({ ...c }); setOpen(true); }} className="p-2 hover:bg-muted rounded-lg">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => onDelete(c.id)} className="p-2 hover:bg-mexred/10 rounded-lg">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <input
                                data-testid="category-name-input"
                                className="w-full px-3 py-2 rounded-xl border border-border"
                                placeholder="Nombre (ej: Chocolates)"
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            />
                            <input
                                className="w-full px-3 py-2 rounded-xl border border-border"
                                placeholder="Emoji (ej: 🍫)"
                                value={editing.emoji || ""}
                                onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    type="number"
                                    className="px-3 py-2 rounded-xl border border-border"
                                    placeholder="Orden"
                                    value={editing.order || 0}
                                    onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                                />
                                <label className="flex items-center gap-2 text-sm bg-muted/30 rounded-xl p-2">
                                    <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                                    Activa
                                </label>
                            </div>
                            <button onClick={onSave} className="w-full bg-mexred text-cocoa rounded-full py-2.5 font-semibold">Guardar</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function NewsletterAdmin() {
    const [subs, setSubs] = useState([]);
    useEffect(() => {
        api.get("/newsletter").then(({ data }) => setSubs(data)).catch(() => {});
    }, []);
    return (
        <div className="space-y-4">
            <h2 className="font-display text-2xl font-semibold">Suscriptores ({subs.length})</h2>
            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">Email</th>
                                <th>Cupón</th>
                                <th>Descuento</th>
                                <th>Origen</th>
                                <th>Usado</th>
                                <th>Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subs.map((s) => (
                                <tr key={s.email} className="border-t border-border/40">
                                    <td className="p-3 font-medium">{s.email}</td>
                                    <td><span className="font-mono text-cocoa font-bold">{s.code}</span></td>
                                    <td>{s.discount_pct}%</td>
                                    <td className="text-xs uppercase">{s.source}</td>
                                    <td>{s.used ? "✅" : "—"}</td>
                                    <td className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("es-CL")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const emptyReward = {
    name: "", description: "", photo_base64: "", points_cost: 100, days_available: 30, active: true,
};

function RewardsAdmin() {
    const [rewards, setRewards] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = () => api.get("/rewards/all").then(({ data }) => setRewards(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("La imagen no puede superar los 3MB");
            return;
        }
        setUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            setEditing((prev) => ({ ...prev, photo_base64: reader.result }));
            setUploading(false);
        };
        reader.onerror = () => {
            toast.error("Error al leer la imagen");
            setUploading(false);
        };
        reader.readAsDataURL(file);
    };

    const onSave = async () => {
        if (!editing.name?.trim()) {
            toast.error("El nombre es obligatorio");
            return;
        }
        if (!editing.photo_base64) {
            toast.error("Debes subir una foto del producto");
            return;
        }
        if (!editing.points_cost || Number(editing.points_cost) < 1) {
            toast.error("Los puntos deben ser al menos 1");
            return;
        }
        try {
            const payload = {
                name: editing.name.trim(),
                description: editing.description || "",
                photo_base64: editing.photo_base64,
                points_cost: Number(editing.points_cost),
                days_available: Number(editing.days_available) || 30,
                active: !!editing.active,
            };
            if (editing.id) await api.put(`/rewards/${editing.id}`, payload);
            else await api.post("/rewards", payload);
            toast.success(editing.id ? "Canjeable actualizado" : "Canjeable creado");
            setOpen(false); setEditing(null); load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleActive = async (rid) => {
        try {
            await api.patch(`/rewards/${rid}/toggle`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("¿Eliminar este canjeable permanentemente? Usa el botón activar/desactivar si solo quieres ocultarlo.")) return;
        try {
            await api.delete(`/rewards/${id}`);
            toast.success("Eliminado");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display text-2xl font-semibold">Canjeables con puntos ({rewards.length})</h2>
                    <p className="text-sm text-muted-foreground">Productos que los clientes pueden canjear con sus puntos acumulados.</p>
                </div>
                <button
                    data-testid="admin-add-reward-btn"
                    onClick={() => { setEditing({ ...emptyReward }); setOpen(true); }}
                    className="bg-mexred hover:bg-mexred_hover text-cocoa rounded-full px-4 py-2 font-semibold flex items-center gap-2 shadow-md"
                >
                    <Plus className="w-4 h-4" /> Nuevo canjeable
                </button>
            </div>

            <div className="bg-white rounded-3xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-muted-foreground">
                            <tr>
                                <th className="p-3">Foto</th>
                                <th>Nombre</th>
                                <th>Puntos</th>
                                <th>Días</th>
                                <th>Estado</th>
                                <th className="text-right pr-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rewards.length === 0 ? (
                                <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">Aún no has creado canjeables.</td></tr>
                            ) : rewards.map((r) => (
                                <tr key={r.id} className="border-t border-border/40" data-testid={`reward-row-${r.id}`}>
                                    <td className="p-2">
                                        {r.photo_base64 ? (
                                            <img src={r.photo_base64} alt={r.name} className="w-14 h-14 object-cover rounded-xl" />
                                        ) : (
                                            <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center">
                                                <Gift className="w-5 h-5 text-muted-foreground" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="font-medium">
                                        {r.name}
                                        {r.description && <div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div>}
                                    </td>
                                    <td className="text-marigold font-bold">{r.points_cost} pts</td>
                                    <td>{r.days_available}</td>
                                    <td>
                                        {r.active ? (
                                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-semibold uppercase">Activo</span>
                                        ) : (
                                            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-semibold uppercase">Inactivo</span>
                                        )}
                                    </td>
                                    <td className="text-right pr-3">
                                        <button
                                            data-testid={`reward-toggle-${r.id}`}
                                            onClick={() => toggleActive(r.id)}
                                            title={r.active ? "Desactivar" : "Activar"}
                                            className="p-2 hover:bg-muted rounded-lg"
                                        >
                                            {r.active ? <PowerOff className="w-4 h-4 text-cocoa" /> : <Power className="w-4 h-4 text-green-600" />}
                                        </button>
                                        <button
                                            data-testid={`reward-edit-${r.id}`}
                                            onClick={() => { setEditing({ ...r }); setOpen(true); }}
                                            className="p-2 hover:bg-muted rounded-lg"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`reward-delete-${r.id}`}
                                            onClick={() => onDelete(r.id)}
                                            className="p-2 hover:bg-mexred/10 hover:text-cocoa rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Editar canjeable" : "Nuevo canjeable"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium text-cocoa">Foto del producto *</label>
                                <div className="mt-1 border-2 border-dashed border-mexred/40 rounded-2xl p-4 bg-mexred/5">
                                    {editing.photo_base64 ? (
                                        <div className="relative">
                                            <img src={editing.photo_base64} alt="preview" className="w-full h-44 object-contain rounded-xl bg-white" />
                                            <button
                                                type="button"
                                                onClick={() => setEditing({ ...editing, photo_base64: "" })}
                                                className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white shadow"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center cursor-pointer py-6">
                                            <ImagePlus className="w-8 h-8 text-cocoa mb-2" />
                                            <span className="text-sm text-cocoa font-medium">
                                                {uploading ? "Cargando..." : "Haz click para subir imagen"}
                                            </span>
                                            <span className="text-xs text-muted-foreground">PNG / JPG · máx 3MB</span>
                                            <input
                                                data-testid="reward-photo-input"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={onFile}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-cocoa">Nombre *</label>
                                <input
                                    data-testid="reward-name-input"
                                    className="mt-1 w-full px-3 py-2 rounded-xl border border-border"
                                    placeholder="Ej. Caja sorpresa Tajín"
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-cocoa">Descripción</label>
                                <textarea
                                    data-testid="reward-description-input"
                                    className="mt-1 w-full px-3 py-2 rounded-xl border border-border min-h-20"
                                    placeholder="Detalle del canjeable"
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-cocoa">Puntos *</label>
                                    <input
                                        data-testid="reward-points-input"
                                        type="number"
                                        min="1"
                                        className="mt-1 w-full px-3 py-2 rounded-xl border border-border"
                                        value={editing.points_cost}
                                        onChange={(e) => setEditing({ ...editing, points_cost: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-cocoa">Días disponibles</label>
                                    <input
                                        data-testid="reward-days-input"
                                        type="number"
                                        min="1"
                                        className="mt-1 w-full px-3 py-2 rounded-xl border border-border"
                                        value={editing.days_available}
                                        onChange={(e) => setEditing({ ...editing, days_available: e.target.value })}
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm bg-muted/30 rounded-xl p-3">
                                <input
                                    data-testid="reward-active-checkbox"
                                    type="checkbox"
                                    checked={!!editing.active}
                                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                />
                                <span className="font-medium">Activo (visible para los clientes)</span>
                            </label>
                            <button
                                data-testid="admin-save-reward-btn"
                                onClick={onSave}
                                className="w-full bg-mexred hover:bg-mexred_hover text-cocoa rounded-full py-2.5 font-semibold shadow-md"
                            >
                                {editing.id ? "Actualizar canjeable" : "Crear canjeable"}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CashRedemptionsAdmin() {
    const [list, setList] = useState([]);

    const load = () => api.get("/redemptions/cash").then(({ data }) => setList(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const markPaid = async (cid) => {
        if (!window.confirm("¿Confirmas que ya realizaste el pago al cliente?")) return;
        try {
            await api.patch(`/redemptions/cash/${cid}`);
            toast.success("Canje marcado como pagado");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const pending = list.filter((r) => r.status === "pending");
    const paid = list.filter((r) => r.status === "paid");

    return (
        <div className="space-y-4">
            <div>
                <h2 className="font-display text-2xl font-semibold">Canjes de puntos por dinero</h2>
                <p className="text-sm text-muted-foreground">1 punto = $10 CLP · máx 1 canje por cliente.</p>
            </div>

            <div className="bg-white rounded-3xl border border-border p-4">
                <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                    <Coins className="w-5 h-5 text-marigold" /> Pendientes de pago ({pending.length})
                </h3>
                {pending.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-2">No hay canjes pendientes.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-left text-muted-foreground">
                                <tr>
                                    <th className="p-2">Cliente</th>
                                    <th>Email</th>
                                    <th>Puntos</th>
                                    <th>Monto</th>
                                    <th>Solicitado</th>
                                    <th className="text-right pr-2">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map((r) => (
                                    <tr key={r.id} className="border-t border-border/40">
                                        <td className="p-2 font-medium">{r.user_name}</td>
                                        <td>{r.user_email}</td>
                                        <td className="text-marigold font-bold">{r.points} pts</td>
                                        <td className="text-cocoa font-bold">{formatCLP(r.amount_clp)}</td>
                                        <td className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("es-CL")}</td>
                                        <td className="text-right pr-2">
                                            <button
                                                data-testid={`cash-mark-paid-${r.id}`}
                                                onClick={() => markPaid(r.id)}
                                                className="bg-green-600 hover:bg-green-700 text-white rounded-full px-3 py-1.5 text-xs font-semibold"
                                            >
                                                Marcar pagado
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-3xl border border-border p-4">
                <h3 className="font-display text-lg font-semibold mb-3">Historial ({paid.length})</h3>
                {paid.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-2">Aún no has pagado canjes.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-left text-muted-foreground">
                                <tr>
                                    <th className="p-2">Cliente</th>
                                    <th>Email</th>
                                    <th>Puntos</th>
                                    <th>Monto</th>
                                    <th>Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paid.map((r) => (
                                    <tr key={r.id} className="border-t border-border/40">
                                        <td className="p-2 font-medium">{r.user_name}</td>
                                        <td>{r.user_email}</td>
                                        <td>{r.points} pts</td>
                                        <td className="text-cocoa">{formatCLP(r.amount_clp)}</td>
                                        <td className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("es-CL")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function NotificationsAdmin() {
    const [items, setItems] = useState([]);

    const load = () => api.get("/notifications").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
    const removeOne = async (id) => { await api.delete(`/notifications/${id}`); load(); };
    const markAll = async () => { await api.post("/notifications/read-all"); load(); };

    const unread = items.filter((n) => !n.read).length;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
                    <Bell className="w-6 h-6 text-mexred" /> Notificaciones {unread > 0 && <span className="bg-mexred text-cocoa px-2 py-0.5 rounded-full text-sm">{unread} nueva{unread > 1 ? "s" : ""}</span>}
                </h2>
                {unread > 0 && (
                    <button onClick={markAll} className="text-sm bg-cocoa text-white rounded-full px-3 py-1.5">Marcar todas leídas</button>
                )}
            </div>
            <div className="bg-white rounded-3xl border border-border divide-y">
                {items.length === 0 && <p className="p-6 text-center text-muted-foreground">Sin notificaciones</p>}
                {items.map((n) => (
                    <div key={n.id} className={`p-4 flex items-start gap-3 ${!n.read ? "bg-amber-50/50" : ""}`}>
                        <div className={`w-2 h-2 rounded-full mt-2 ${!n.read ? "bg-mexred" : "bg-gray-300"}`}></div>
                        <div className="flex-1">
                            <div className="font-semibold text-cocoa">{n.title}</div>
                            <div className="text-sm text-muted-foreground">{n.message}</div>
                            <div className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("es-CL")}</div>
                        </div>
                        <div className="flex gap-1">
                            {!n.read && <button onClick={() => markRead(n.id)} className="text-xs px-2 py-1 rounded-lg bg-cocoa text-white">Leer</button>}
                            <button onClick={() => removeOne(n.id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4"/></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}




export default function Admin() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
            <div className="mb-6">
                <span className="text-marigold font-semibold uppercase tracking-widest text-xs">Panel</span>
                <h1 className="font-display text-4xl font-semibold text-cocoa">Administración</h1>
            </div>
            <Tabs defaultValue="dashboard">
                <TabsList className="bg-white p-1 rounded-2xl shadow-sm flex flex-wrap h-auto">
                    <TabsTrigger value="dashboard" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><BarChart3 className="w-4 h-4" /> Dashboard</TabsTrigger>
                    <TabsTrigger value="orders" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><ShoppingBag className="w-4 h-4" /> Pedidos</TabsTrigger>
                    <TabsTrigger value="products" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Package className="w-4 h-4" /> Productos</TabsTrigger>
                    <TabsTrigger value="categories" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1" data-testid="admin-tab-categories"><FolderTree className="w-4 h-4" /> Categorías</TabsTrigger>
                    <TabsTrigger value="rewards" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1" data-testid="admin-tab-rewards"><Gift className="w-4 h-4" /> Canjeables</TabsTrigger>
                    <TabsTrigger value="cash" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1" data-testid="admin-tab-cash"><Coins className="w-4 h-4" /> Canje efectivo</TabsTrigger>
                    <TabsTrigger value="users" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Users className="w-4 h-4" /> Usuarios</TabsTrigger>
                    <TabsTrigger value="branches" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Building2 className="w-4 h-4" /> Sucursales</TabsTrigger>
                    <TabsTrigger value="notifications" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Bell className="w-4 h-4" /> Avisos</TabsTrigger>
                    <TabsTrigger value="newsletter" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Mail className="w-4 h-4" /> Newsletter</TabsTrigger>
                    <TabsTrigger value="settings" className="data-[state=active]:bg-mexred data-[state=active]:text-cocoa rounded-xl gap-1"><Settings className="w-4 h-4" /> Config</TabsTrigger>
                </TabsList>
                <TabsContent value="dashboard" className="mt-6"><Dashboard /></TabsContent>
                <TabsContent value="orders" className="mt-6"><OrdersAdmin /></TabsContent>
                <TabsContent value="products" className="mt-6"><ProductsAdmin /></TabsContent>
                <TabsContent value="categories" className="mt-6"><CategoriesAdmin /></TabsContent>
                <TabsContent value="rewards" className="mt-6"><RewardsAdmin /></TabsContent>
                <TabsContent value="cash" className="mt-6"><CashRedemptionsAdmin /></TabsContent>
                <TabsContent value="users" className="mt-6"><UsersAdmin /></TabsContent>
                <TabsContent value="branches" className="mt-6"><BranchesAdmin /></TabsContent>
                <TabsContent value="notifications" className="mt-6"><NotificationsAdmin /></TabsContent>
                <TabsContent value="newsletter" className="mt-6"><NewsletterAdmin /></TabsContent>
                <TabsContent value="settings" className="mt-6"><SettingsAdmin /></TabsContent>
            </Tabs>
        </div>
    );
}
